import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCamera, getCameraConnection,
  startCamera, stopCamera, restartCamera, deleteCamera, cancelDeleteCamera,
} from '../../services/cameras';
import { ArrowLeft } from '@gravity-ui/icons';
import { isAdmin } from '../../services/api';
import { deriveCameraDisplayStatus } from '../../utils/cameraHealth';
import { usePolling } from '../../hooks/usePolling';
import CameraDiagnosticsPanel from '../../components/CameraDiagnostics/CameraDiagnosticsPanel';
import CameraEventHistory from '../../components/CameraEventHistory/CameraEventHistory';
import CameraNotificationGroups from '../../components/CameraNotificationGroups/CameraNotificationGroups';
import CameraNumber from '../../components/CameraNumber/CameraNumber';
import './CameraDetailPage.css';

const TABS = { DIAG: 'diagnostics', EVENTS: 'events', NOTIF: 'notifications' };

/**
 * @typedef {Object} CameraHealthSummary
 * @property {boolean} [desired_running]
 * @property {string} [connection_status]
 * @property {string|null} [incident_id]
 * @property {number|null} [offline_duration_seconds]
 * @property {number} [retry_count]
 * @property {string|null} [next_retry_at]
 *
 * @typedef {Object} Camera
 * @property {number} id
 * @property {string} name
 * @property {string} [status]
 * @property {string} [display_timezone]
 * @property {CameraHealthSummary|null} [health_summary]
 *
 * @typedef {Object.<string, any>} CameraConnection
 */

const CameraDetailPage = ({ cameraType = 'PEOPLE' }) => {
  const { cameraId } = useParams();
  const navigate = useNavigate();
  const admin = isAdmin();
  const basePath = cameraType === 'EQUIPMENT' ? 'equipment' : 'cameras';

  const [camera, setCamera] = useState(/** @type {Camera|null} */ (null));
  const [connection, setConnection] = useState(/** @type {CameraConnection|null} */ (null));
  const [tab, setTab] = useState(TABS.DIAG);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const loadCamera = useCallback(() => {
    getCamera(cameraId)
      .then((cam) => { setCamera(cam); setError(''); })
      .catch((err) => {
        if (err?.response?.status === 404) {
          setError('Камера недоступна');
          setTimeout(() => {
            if (!mountedRef.current) return;
            navigate(`/${basePath}`);
          }, 1500);
        } else {
          setError('Данные временно недоступны');
        }
      });
  }, [cameraId, navigate, basePath]);

  const loadConnection = useCallback(() => {
    getCameraConnection(cameraId).then(setConnection).catch(() => {});
  }, [cameraId]);

  useEffect(() => { loadCamera(); loadConnection(); }, [loadCamera, loadConnection]);

  const connStatus = (connection?.connection_status || camera?.health_summary?.connection_status || '').toUpperCase();
  const fastPoll = connStatus === 'RETRYING' || connStatus === 'RETRY_WAIT';
  usePolling(loadConnection, fastPoll ? 12000 : 45000, Boolean(camera) && tab === TABS.DIAG);

  // «Назад» — на предыдущую страницу (админка, «Кадры», «Оборудование» — откуда
  // пришли). Если истории нет (страницу открыли по прямой ссылке), уходим в список.
  const handleBack = () => {
    const idx = window.history.state?.idx;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      navigate(`/${basePath}`);
    }
  };

  const refreshAfterAction = () => { loadCamera(); loadConnection(); setRefreshKey((k) => k + 1); };

  const runAction = (fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    fn(cameraId)
      .then(() => {
        setTimeout(() => {
          if (!mountedRef.current) return;
          refreshAfterAction();
        }, 800);
      })
      .catch(() => setError('Не удалось выполнить действие'))
      .finally(() => setBusy(false));
  };

  if (error && !camera) return <div className="p-4 text-danger">{error}</div>;
  if (!camera) return <div className="p-4 text-muted">Загрузка…</div>;

  // health_summary в ответе GET /cameras/{id} может быть null — тогда статус
  // берём из ответа /connection, который на этой странице уже загружен.
  const { text, color } = deriveCameraDisplayStatus(camera, connection);
  const isPendingDelete = camera.status === 'PENDING_DELETE';

  const actionsSlot = (
    <div className="d-flex gap-2 flex-wrap">
      <button type="button" className="btn btn-sm btn-success" disabled={busy} onClick={() => runAction(startCamera)}>Запустить</button>
      <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={() => runAction(stopCamera, 'Остановить камеру?')}>Остановить</button>
      <button type="button" className="btn btn-sm btn-warning" disabled={busy} onClick={() => runAction(restartCamera, 'Перезапустить камеру?')}>Перезапустить</button>
      {admin && !isPendingDelete && (
        <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy}
          onClick={() => runAction(deleteCamera, 'Запланировать удаление камеры (PENDING_DELETE)?')}>Удалить</button>
      )}
      {admin && isPendingDelete && (
        <button type="button" className="btn btn-sm btn-outline-primary" disabled={busy}
          onClick={() => runAction(cancelDeleteCamera)}>Отменить удаление</button>
      )}
    </div>
  );

  return (
    <div className="camera-detail-page p-3">
      <div className="camera-detail-header mb-3">
        <button type="button" className="btn btn-sm btn-outline-secondary cd-back-btn" onClick={handleBack}><ArrowLeft className="g-icon-lead" width={16} height={16} />Назад</button>
        <h4 className="cd-title mb-0">
          {camera.name}
          <CameraNumber id={camera.id} />
        </h4>
        <span className={`badge bg-${color}`}>{text}</span>
      </div>

      {error && <div className="alert alert-warning py-2">{error}</div>}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === TABS.DIAG ? 'active' : ''}`} onClick={() => setTab(TABS.DIAG)}>Диагностика</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === TABS.EVENTS ? 'active' : ''}`} onClick={() => setTab(TABS.EVENTS)}>История событий</button>
        </li>
        {admin && (
          <li className="nav-item">
            <button className={`nav-link ${tab === TABS.NOTIF ? 'active' : ''}`} onClick={() => setTab(TABS.NOTIF)}>Уведомления</button>
          </li>
        )}
      </ul>

      {tab === TABS.DIAG && (
        <CameraDiagnosticsPanel connection={connection} displayTimezone={camera.display_timezone} actionsSlot={actionsSlot} />
      )}
      {tab === TABS.EVENTS && (
        <CameraEventHistory cameraId={cameraId} displayTimezone={camera.display_timezone} refreshKey={refreshKey} />
      )}
      {tab === TABS.NOTIF && admin && (
        <CameraNotificationGroups cameraId={cameraId} />
      )}
    </div>
  );
};

export default CameraDetailPage;
